import { format, parseISO } from 'date-fns';

// IST Timezone constant
export const IST_TIMEZONE = 'Asia/Kolkata';
export const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000; // For backward compatibility

// Helper to get IST date components for comparisons
const getISTDateComponents = (date: Date | string) => {
  const d = typeof date === 'string' ? new Date(date) : date;
  const formatter = new Intl.DateTimeFormat('en-IN', {
    timeZone: IST_TIMEZONE,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  });
  const parts = formatter.formatToParts(d);
  const year = parseInt(parts.find(p => p.type === 'year')?.value || '0');
  const month = parseInt(parts.find(p => p.type === 'month')?.value || '0');
  const day = parseInt(parts.find(p => p.type === 'day')?.value || '0');
  return { year, month, day };
};

// Helper to get current IST date
export const getISTDate = (): Date => {
  const now = new Date();
  // Convert to IST by adding offset
  const istTime = new Date(now.getTime() + IST_OFFSET_MS - (now.getTimezoneOffset() * 60 * 1000));
  return istTime;
};

// Helper to get IST date string (YYYY-MM-DD)
export const getISTDateString = (date: Date): string => {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: IST_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return formatter.format(date);
};

// Format currency
export const formatCurrency = (amount: number): string => {
  const formatted = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
  }).format(amount);
  
  // Ensure we always display ₹ symbol, replacing any $ that might appear
  return formatted.replace(/\$/g, '₹').replace(/INR/g, '₹');
};

// Format date (displays in IST)
export const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: IST_TIMEZONE,
    });
  } catch {
    return dateString;
  }
};

// Format time (displays in IST)
export const formatTime = (timeString: string): string => {
  const [hours, minutes] = timeString.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes} ${ampm}`;
};

// Format date and time (displays in IST)
export const formatDateTime = (dateString: string, timeString?: string): string => {
  try {
    let date: Date;
    
    if (timeString) {
      // Parse date and time separately for better handling
      const dateObj = new Date(dateString);
      const [hours, minutes] = timeString.split(':').map(Number);
      
      // Combine date and time properly
      const combined = new Date(dateObj);
      combined.setHours(hours, minutes, 0, 0);
      date = combined;
    } else {
      date = new Date(dateString);
    }
    
    if (isNaN(date.getTime())) return timeString ? `${dateString} ${timeString}` : dateString;
    
    // Format with proper date and 12-hour time with AM/PM
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: IST_TIMEZONE,
    });
  } catch {
    return timeString ? `${dateString} ${timeString}` : dateString;
  }
};

// Calculate time ago
export const getTimeAgo = (dateString: string): string => {
  const now = new Date();
  const date = new Date(dateString);
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'Just now';
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours}h ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays}d ago`;
  }

  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4) {
    return `${diffInWeeks}w ago`;
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  return `${diffInMonths}mo ago`;
};

// Get status color
export const getStatusColor = (status: string): string => {
  // Normalize status to handle variations
  const normalizedStatus = status.toLowerCase().replace(/\s+/g, '-').trim();
  
  switch (normalizedStatus) {
    case 'pending':
      return '#6b7280'; // Gray - neutral
    case 'confirmed':
      return '#3b82f6'; // Blue - confirmed/positive
    case 'assigned':
    case 'professional-assigned':
      return '#8b5cf6'; // Purple - assigned
    case 'professional-en-route':
      return '#f59e0b'; // Amber/Orange - in transit
    case 'in-progress':
      return '#f59e0b'; // Amber/Orange - active work
    case 'completed':
      return '#22c55e'; // Green - success/completed
    case 'cancelled':
      return '#ef4444'; // Red - cancelled/error
    case 'rescheduled':
      return '#06b6d4'; // Cyan - rescheduled
    default:
      return '#6b7280'; // Gray - default/unknown
  }
};

// Get status text
export const getStatusText = (status: string): string => {
  switch (status) {
    case 'pending':
      return 'Pending';
    case 'confirmed':
      return 'Confirmed';
    case 'assigned':
      return 'Assigned';
    case 'professional-assigned':
      return 'Professional Assigned';
    case 'professional-en-route':
      return 'Professional En Route';
    case 'in-progress':
      return 'In Progress';
    case 'completed':
      return 'Completed';
    case 'cancelled':
      return 'Cancelled';
    default:
      return 'Unknown';
  }
};

// Get initials from name
export const getInitials = (name: string | undefined | null): string => {
  if (!name) return 'NA';
  return name
    .split(' ')
    .map(word => word.charAt(0).toUpperCase())
    .join('')
    .substr(0, 2);
};

// Validate email
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Validate phone number
export const isValidPhone = (phone: string): boolean => {
  const phoneRegex = /^[6-9]\d{9}$/;
  return phoneRegex.test(phone);
};

// Truncate text
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substr(0, maxLength) + '...';
};

// Capitalize first letter
export const capitalize = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

// Check if date is today (using IST)
export const isToday = (dateString: string): boolean => {
  const today = getISTDateComponents(new Date());
  const date = getISTDateComponents(dateString);
  return (
    date.day === today.day &&
    date.month === today.month &&
    date.year === today.year
  );
};

// Check if date is tomorrow (using IST)
export const isTomorrow = (dateString: string): boolean => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowIST = getISTDateComponents(tomorrow);
  const date = getISTDateComponents(dateString);
  return (
    date.day === tomorrowIST.day &&
    date.month === tomorrowIST.month &&
    date.year === tomorrowIST.year
  );
};

// Deep clone object
export const deepClone = <T>(obj: T): T => {
  if (obj === null || typeof obj !== 'object') return obj;
  if (obj instanceof Date) return new Date(obj.getTime()) as any;
  if (obj instanceof Array) return obj.map(item => deepClone(item)) as any;
  if (typeof obj === 'object') {
    const clonedObj = {} as any;
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        clonedObj[key] = deepClone(obj[key]);
      }
    }
    return clonedObj;
  }
  return obj;
};

// Check if object is empty
export const isEmpty = (obj: any): boolean => {
  if (obj == null) return true;
  if (typeof obj === 'string') return obj.length === 0;
  if (Array.isArray(obj)) return obj.length === 0;
  if (typeof obj === 'object') return Object.keys(obj).length === 0;
  return false;
};

// Debounce function
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// Throttle function
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

// CSV export
export const exportToCSV = (filename: string, headers: string[], rows: Array<Array<string | number | boolean | null | undefined>>) => {
  const csvRows = [headers].concat(rows).map(r => r.map(v => `"${String(v ?? '').replace(/"/g,'""')}"`).join(',')).join('\n');
  const blob = new Blob([csvRows], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
};

// Simple in-app event bus to simulate realtime updates
type EventHandler<T> = (payload: T) => void;
const channels: Record<string, Set<EventHandler<any>>> = {};
export const rtSubscribe = <T = any>(channel: string, handler: EventHandler<T>) => {
  if (!channels[channel]) channels[channel] = new Set();
  channels[channel].add(handler);
  return () => channels[channel].delete(handler);
};
export const rtPublish = <T = any>(channel: string, payload: T) => {
  (channels[channel] || new Set()).forEach(h => h(payload));
};

// Simple client-side audit log using localStorage
type AuditRecord = { id: string; at: string; actor: string; action: string; entity?: string; meta?: any };
const AUDIT_KEY = 'admin_audit_logs';
export const logAudit = (action: string, entity?: string, meta?: any) => {
  try {
    const rec: AuditRecord = { id: `log_${Date.now()}`, at: new Date().toISOString(), actor: 'admin', action, entity, meta };
    const raw = localStorage.getItem(AUDIT_KEY);
    const list: AuditRecord[] = raw ? JSON.parse(raw) : [];
    list.unshift(rec);
    localStorage.setItem(AUDIT_KEY, JSON.stringify(list.slice(0, 500)));
    rtPublish('audit:new', rec);
  } catch {}
};
export const getAuditLogs = (): AuditRecord[] => {
  try { return JSON.parse(localStorage.getItem(AUDIT_KEY) || '[]'); } catch { return []; }
};

// Image URL utility
export const getImageUrl = (imagePath?: string | null): string | null => {
  if (!imagePath) return null;
  // If it's a base64 data URL, return as is
  if (imagePath.startsWith('data:')) return imagePath;
  // If it's already a full URL, return as is
  if (imagePath.startsWith('http')) return imagePath;
  // Get the server base URL from environment variable or fallback
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'https://api.spotlessolutions.in/api';
  const serverBaseUrl = apiBaseUrl.replace(/\/api$/, '');
  // If it starts with /uploads, construct the full URL
  if (imagePath.startsWith('/uploads')) {
    return `${serverBaseUrl}${imagePath}`;
  }
  // Otherwise, assume it's a relative path and construct the full URL
  return `${serverBaseUrl}/uploads/${imagePath}`;
};

// Convert base64 data URI to blob URL for opening in new tab
export const dataUriToBlobUrl = (dataUri: string): string | null => {
  try {
    if (!dataUri || !dataUri.startsWith('data:')) return null;
    
    // Parse the data URI
    const [header, base64Data] = dataUri.split(',');
    if (!base64Data) return null;
    
    const mimeMatch = header.match(/data:([^;]+)/);
    const mimeType = mimeMatch ? mimeMatch[1] : 'image/jpeg';
    
    // Convert base64 to binary
    const binaryString = atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    
    // Create blob and return blob URL
    const blob = new Blob([bytes], { type: mimeType });
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('Error converting data URI to blob URL:', error);
    return null;
  }
};

// Handle opening image in new tab (works for both data URIs and regular URLs)
export const openImageInNewTab = (imageUrl: string | null) => {
  if (!imageUrl) return;
  
  // If it's a data URI, convert to blob URL first
  if (imageUrl.startsWith('data:')) {
    const blobUrl = dataUriToBlobUrl(imageUrl);
    if (blobUrl) {
      const newWindow = window.open(blobUrl, '_blank', 'noopener,noreferrer');
      // Clean up blob URL after a delay (give browser time to load)
      if (newWindow) {
        setTimeout(() => {
          URL.revokeObjectURL(blobUrl);
        }, 1000);
      } else {
        // If popup blocked, cleanup immediately
        if (blobUrl) URL.revokeObjectURL(blobUrl);
      }
    }
  } else {
    // Regular URL, just open it
    window.open(imageUrl, '_blank', 'noopener,noreferrer');
  }
};

// RBAC utilities
export const PERMISSIONS = {
  DASHBOARD_READ: 'dashboard.read',
  DASHBOARD_WRITE: 'dashboard.write',
  USERS_READ: 'users.read',
  USERS_WRITE: 'users.write',
  USERS_DELETE: 'users.delete',
  SERVICES_READ: 'services.read',
  SERVICES_WRITE: 'services.write',
  SERVICES_DELETE: 'services.delete',
  BOOKINGS_READ: 'bookings.read',
  BOOKINGS_WRITE: 'bookings.write',
  BOOKINGS_DELETE: 'bookings.delete',
  PROFESSIONALS_READ: 'professionals.read',
  PROFESSIONALS_WRITE: 'professionals.write',
  PROFESSIONALS_DELETE: 'professionals.delete',
  WORKERS_READ: 'workers.read',
  WORKERS_WRITE: 'workers.write',
  WORKERS_DELETE: 'workers.delete',
  WORKERS_APPROVE: 'workers.approve',
  WORKERS_REJECT: 'workers.reject',
  BLOG_READ: 'blog.read',
  BLOG_WRITE: 'blog.write',
  BLOG_DELETE: 'blog.delete',
  BLOG_CATEGORIES_READ: 'blog.categories.read',
  BLOG_CATEGORIES_WRITE: 'blog.categories.write',
  BLOG_CATEGORIES_DELETE: 'blog.categories.delete',
  SLIDESHOW_READ: 'slideshow.read',
  SLIDESHOW_WRITE: 'slideshow.write',
  SLIDESHOW_DELETE: 'slideshow.delete',
  COUPONS_READ: 'coupons.read',
  COUPONS_WRITE: 'coupons.write',
  COUPONS_DELETE: 'coupons.delete',
  WORKER_OPS_READ: 'workerOps.read',
  WORKER_OPS_WRITE: 'workerOps.write',
  FINANCE_READ: 'finance.read',
  FINANCE_WRITE: 'finance.write',
  CUSTOMER_EXP_READ: 'customerExp.read',
  CUSTOMER_EXP_WRITE: 'customerExp.write',
  MARKETING_READ: 'marketing.read',
  MARKETING_WRITE: 'marketing.write',
  REVIEWS_READ: 'reviews.read',
  REVIEWS_WRITE: 'reviews.write',
  NOTIFICATIONS_READ: 'notifications.read',
  NOTIFICATIONS_WRITE: 'notifications.write',
  ANALYTICS_READ: 'analytics.read',
  PAYMENTS_READ: 'payments.read',
  PAYMENTS_WRITE: 'payments.write',
  REFERRALS_READ: 'referrals.read',
  REFERRALS_WRITE: 'referrals.write',
  COMMUNICATION_READ: 'communication.read',
  COMMUNICATION_WRITE: 'communication.write',
  SUPPORT_READ: 'support.read',
  SUPPORT_WRITE: 'support.write',
  TICKETS_READ: 'tickets.read',
  TICKETS_WRITE: 'tickets.write',
  AUDIT_READ: 'audit.read',
  SETTINGS_READ: 'settings.read',
  SETTINGS_WRITE: 'settings.write',
  ADMIN_ACCESS_READ: 'adminAccess.read',
  ADMIN_ACCESS_WRITE: 'adminAccess.write',
  RATINGS_READ: 'ratings.read',
  RATINGS_WRITE: 'ratings.write',
  RATINGS_APPROVE: 'ratings.approve',
  RATINGS_REJECT: 'ratings.reject',
  SERVICE_CATEGORIES_READ: 'serviceCategories.read',
  SERVICE_CATEGORIES_WRITE: 'serviceCategories.write',
  SERVICE_CATEGORIES_DELETE: 'serviceCategories.delete',
  ADMIN_MANAGE: 'admin.manage',
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export const ROLE_PERMISSIONS: Record<string, Permission[]> = {
  'super-admin': Object.values(PERMISSIONS),
  'admin': [
    PERMISSIONS.USERS_READ,
    PERMISSIONS.USERS_WRITE,
    PERMISSIONS.SERVICES_READ,
    PERMISSIONS.SERVICES_WRITE,
    PERMISSIONS.BOOKINGS_READ,
    PERMISSIONS.BOOKINGS_WRITE,
    PERMISSIONS.PROFESSIONALS_READ,
    PERMISSIONS.PROFESSIONALS_WRITE,
    PERMISSIONS.BLOG_READ,
    PERMISSIONS.BLOG_WRITE,
    PERMISSIONS.MARKETING_WRITE,
    PERMISSIONS.NOTIFICATIONS_WRITE,
    PERMISSIONS.ANALYTICS_READ,
    PERMISSIONS.PAYMENTS_READ,
    PERMISSIONS.FINANCE_WRITE,
    PERMISSIONS.REFERRALS_WRITE,
    PERMISSIONS.SUPPORT_WRITE,
    PERMISSIONS.SETTINGS_WRITE,
    PERMISSIONS.AUDIT_READ,
    PERMISSIONS.WORKER_OPS_WRITE,
    PERMISSIONS.SLIDESHOW_WRITE,
    PERMISSIONS.COUPONS_WRITE,
  ],
  'manager': [
    PERMISSIONS.USERS_READ,
    PERMISSIONS.SERVICES_READ,
    PERMISSIONS.BOOKINGS_READ,
    PERMISSIONS.BOOKINGS_WRITE,
    PERMISSIONS.PROFESSIONALS_READ,
    PERMISSIONS.BLOG_READ,
    PERMISSIONS.ANALYTICS_READ,
    PERMISSIONS.PAYMENTS_READ,
    PERMISSIONS.SUPPORT_WRITE,
  ],
  'moderator': [
    PERMISSIONS.USERS_READ,
    PERMISSIONS.BOOKINGS_READ,
    PERMISSIONS.SUPPORT_WRITE,
  ],
  'support': [
    PERMISSIONS.USERS_READ,
    PERMISSIONS.BOOKINGS_READ,
    PERMISSIONS.SUPPORT_WRITE,
  ],
};

export const hasPermission = (userPermissions: string[]|undefined, required: Permission|string|Array<Permission|string>): boolean => {
  if (!required) return true;
  const req = Array.isArray(required) ? required : [required];
  const perms = new Set(userPermissions||[]);
  return req.every(p => perms.has(p));
};
